// Import React
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Import hooks
import { usePercentTransformEditor } from "../hooks/usePercentTransformEditor";
import { useArrowKeyNudge } from "../hooks/useArrowKeyNudge";
import useIsMobile from "global/hooks/useIsMobile";

// Import components
import { ColorPickerSmall } from "global/components/ColourPicker";

// Import helpers
import { BuildSingleImageAsset } from "../utilities/BuildSingleImageAsset";
import {
    normalisePositionValue,
    resolvePlacementTransform,
    getAngleFromPoints,
    snapRotateDegrees,
    getRotatedCornerCursor,
} from "../utilities/ImagePlacementEditorHelpers";

// Import styles
import "../css/ImagePlacementEditor.css";
import CreateButton from "global/components/CreateButton";



// Export the component
export const ImagePlacementEditor = ({

    // The product (parent) sku
    productSku,

    // The child sku (if there is one)
    childSku,

    // The role code
    role,

    // The role active state
    active,

    // The preview asset object
    asset,

    // The print area settings
    printAreaSettings,

    // The user design image
    userImageUrl,

    // Fit/fill always comes from the design role
    fit_fill,

    // Natural image width
    naturalWidth,

    // Natural image height
    naturalHeight,

    // Child role data override
    childRoleData,

    // Parent role data fallback
    parentRoleData,

    // Design role data fallback
    designRoleData,

    // Callback fired whenever the user moves/scales
    onTransformChange,

    // Callback used by the parent footer to trigger a placement reset
    onResetPlacementReady,

}) => {


    // Set isMobile from use hook 
    const isMobile = useIsMobile(1100);


    // This reads the safe role key
    const safeRoleKey = String(role || "");

    // This resolves one transform in priority order
    const resolvedTransform = useMemo(() => {

        // This resolves the transform
        return resolvePlacementTransform({
            role: safeRoleKey,
            fitFill: fit_fill,
            childRoleData: childRoleData,
            parentRoleData: parentRoleData,
            designRoleData: designRoleData,
            childSku: childSku,
        });

    }, [
        safeRoleKey,
        fit_fill,
        childRoleData,
        parentRoleData,
        designRoleData,
        childSku,
    ]);



    // This reads resolved background colour
    const resolvedBackgroundColour = resolvedTransform?.background_colour || null;


    // This uses the simplified hook only
    const {
        transform,
        setCentreX,
        setCentreY,
        setUniformScale,
        setPosition,
        setBackgroundColour,
        nudgeTransform,
        resetTransform,
        setRotate,
        applyTransformPatch,
    } = usePercentTransformEditor({
        productSku: productSku,
        childSku: childSku,
        role: safeRoleKey,
        active: active,
        fitFill: fit_fill,
        childRoleData: childRoleData,
        parentRoleData: parentRoleData,
        designRoleData: designRoleData,
        onTransformChange: onTransformChange,
    });



    // This builds a fallback asset if none is provided
    const singleImageAsset = useMemo(() => {

        // If a real asset exists do not build a fallback
        if (asset) {

            // This returns null
            return null;

        }

        // This builds a fallback
        return BuildSingleImageAsset({
            naturalWidth: naturalWidth,
            naturalHeight: naturalHeight,
            imageUrl: userImageUrl,
        });

    }, [
        asset,
        naturalWidth,
        naturalHeight,
        userImageUrl,
    ]);



    // This is the asset we will actually use
    const effectiveAsset = asset || singleImageAsset;



    // Ref for the editor canvas viewport
    const editorRef = useRef(null);

    // Ref for the stage element
    const stageRef = useRef(null);

    // Store the measured available stage size in px
    const [stageBoundsPx, setStageBoundsPx] = useState({
        width: 0,
        height: 0,
    });

    // Ref for the user image wrapper
    const userRef = useRef(null);

    // Ref for the selection overlay
    const selectionRef = useRef(null);

    // This stores resize state for corner handle scaling
    const resizeRef = useRef(null);

    // This stores rotate state
    const rotateRef = useRef(null);

    // Ref for the rotate handle
    const rotateHandleRef = useRef(null);

    // This stores whether the rotate handle is being dragged
    const [isRotating, setIsRotating] = useState(false);



    // This is the minimum real scale
    const MIN_SCALE = 0.02;

    // This is the default real scale
    const DEFAULT_SCALE = 1;

    // This is the maximum real scale
    const MAX_SCALE = 12;

    // This is the complete internal slider range
    const SLIDER_MAX_VALUE = 1000;

    // This keeps 100 percent scale in the middle of the slider
    const SLIDER_MIDPOINT_VALUE = 500;



    // This converts a real scale into a high-resolution slider value
    const scaleToSliderValue = (scaleValue) => {

        // Convert the supplied scale safely
        let safeScale = Number(scaleValue);

        // Fall back to the normal scale when the value is invalid
        if (!Number.isFinite(safeScale)) {
            safeScale = DEFAULT_SCALE;
        }

        // Keep the scale inside the supported range
        safeScale = Math.max(
            MIN_SCALE,
            Math.min(MAX_SCALE, safeScale)
        );

        // Map the lower scale range onto the first half of the slider
        if (safeScale <= DEFAULT_SCALE) {

            return (
                (safeScale - MIN_SCALE) /
                (DEFAULT_SCALE - MIN_SCALE)
            ) * SLIDER_MIDPOINT_VALUE;

        }

        // Work out the size of the upper half of the slider
        const upperSliderRange =
            SLIDER_MAX_VALUE - SLIDER_MIDPOINT_VALUE;

        // Map the upper scale range onto the second half of the slider
        return SLIDER_MIDPOINT_VALUE + (
            (
                safeScale - DEFAULT_SCALE
            ) /
            (
                MAX_SCALE - DEFAULT_SCALE
            )
        ) * upperSliderRange;

    };



    // This converts a high-resolution slider value back into real scale
    const sliderValueToScale = (sliderValue) => {

        // Convert the supplied slider value safely
        let safeSliderValue = Number(sliderValue);

        // Fall back to the slider midpoint when the value is invalid
        if (!Number.isFinite(safeSliderValue)) {
            safeSliderValue = SLIDER_MIDPOINT_VALUE;
        }

        // Keep the slider value inside its supported range
        safeSliderValue = Math.max(
            0,
            Math.min(SLIDER_MAX_VALUE, safeSliderValue)
        );

        // Map the first half of the slider onto the lower scale range
        if (safeSliderValue <= SLIDER_MIDPOINT_VALUE) {

            return MIN_SCALE + (
                (
                    safeSliderValue /
                    SLIDER_MIDPOINT_VALUE
                ) *
                (
                    DEFAULT_SCALE - MIN_SCALE
                )
            );

        }

        // Work out the size of the upper half of the slider
        const upperSliderRange =
            SLIDER_MAX_VALUE - SLIDER_MIDPOINT_VALUE;

        // Map the second half of the slider onto the upper scale range
        return DEFAULT_SCALE + (
            (
                (
                    safeSliderValue -
                    SLIDER_MIDPOINT_VALUE
                ) /
                upperSliderRange
            ) *
            (
                MAX_SCALE - DEFAULT_SCALE
            )
        );

    };



    // Read canvas width safely
    const canvasWidth = Number(effectiveAsset?.canvas_width || 0);

    // Read canvas height safely
    const canvasHeight = Number(effectiveAsset?.canvas_height || 0);

    // Extract layers safely
    const layers = useMemo(() => {

        // Return existing layers or a stable empty array
        return Array.isArray(effectiveAsset?.layers) ? effectiveAsset.layers : [];

    }, [effectiveAsset?.layers]);

    // Extract positions safely
    const positions = useMemo(() => {

        // Return existing positions or a stable empty array
        return Array.isArray(effectiveAsset?.positions) ? effectiveAsset.positions : [];

    }, [effectiveAsset?.positions]);



    // Find background layer
    const backgroundLayer = useMemo(() => {

        // This returns the background layer
        return layers.find((layer) => layer?.type === "background") || null;

    }, [layers]);



    // Find trim layer
    const trimLayer = useMemo(() => {

        // This returns the trim layer
        return layers.find((layer) => layer?.type === "trim") || null;

    }, [layers]);



    // This stores only the explicit saved/selected position
    const [selectedPositionCode, setSelectedPositionCode] = useState(
        normalisePositionValue(resolvedTransform?.position)
    );



    // This stores whether the user has reset back to automatic fit/fill positioning
    const [useAutomaticPosition, setUseAutomaticPosition] = useState(false);



    // This keeps selected position in sync when the loaded source changes
    useEffect(() => {

        // This syncs the position
        setSelectedPositionCode(normalisePositionValue(resolvedTransform?.position));

        // This allows loaded data to use saved position again
        setUseAutomaticPosition(false);

    }, [resolvedTransform?.position]);



    // This works out the preferred automatic position for fit/fill
    const automaticPositionCode = useMemo(() => {

        // If there are no positions return null
        if (!positions.length) {

            // This returns null
            return null;

        }

        // If the live transform is in fit mode, prefer the fit position
        if (transform?.fit_fill === "fit") {

            // This returns preferred fit or first available
            return (
                positions.find((positionItem) => positionItem?.preferred_fit === "Y")?.code ||
                positions?.[0]?.code ||
                null
            );

        }

        // Otherwise return preferred fill or first available
        return (
            positions.find((positionItem) => positionItem?.preferred_fill === "Y")?.code ||
            positions?.[0]?.code ||
            null
        );

    }, [
        transform?.fit_fill,
        positions,
    ]);



    // This is the actual position used to render the image
    const effectivePositionCode = useMemo(() => {

        // If reset has been clicked, use the current fit/fill automatic logic
        if (useAutomaticPosition) {

            // This returns the current fit/fill default position
            return automaticPositionCode;

        }

        // If the user explicitly selected one use it
        if (selectedPositionCode) {

            // This returns the explicit position
            return selectedPositionCode;

        }

        // Otherwise use the incoming saved position if there is one
        const incomingPosition = normalisePositionValue(resolvedTransform?.position);

        // If there is a saved position use it
        if (incomingPosition) {

            // This returns it
            return incomingPosition;

        }

        // Otherwise use automatic fit/fill logic
        return automaticPositionCode;

    }, [
        useAutomaticPosition,
        selectedPositionCode,
        resolvedTransform?.position,
        automaticPositionCode,
    ]);



    // Find selected position object for rendering
    const positionToUse = useMemo(() => {

        // This finds the position object
        return positions.find((positionItem) => positionItem?.code === effectivePositionCode) || null;

    }, [
        positions,
        effectivePositionCode,
    ]);



    // Read selected position bounds
    const positionBounds = useMemo(() => {

        // This reads top left x
        const topLeftX = Number(positionToUse?.top_left_x ?? 0);

        // This reads top left y
        const topLeftY = Number(positionToUse?.top_left_y ?? 0);

        // This reads width
        const width = Number(positionToUse?.width ?? canvasWidth);

        // This reads height
        const height = Number(positionToUse?.height ?? canvasHeight);

        // This returns bounds
        return {
            topLeftX: topLeftX,
            topLeftY: topLeftY,
            width: width,
            height: height,
        };

    }, [
        positionToUse,
        canvasWidth,
        canvasHeight,
    ]);



    // Measure the available stage size
    useEffect(() => {

        // This reads the stage node
        const node = stageRef.current;

        // This guards
        if (!node) return;

        // This creates the observer
        const observer = new ResizeObserver((entries) => {

            // This reads the first entry
            const entry = entries?.[0];

            // This guards
            if (!entry) return;

            // This reads the available width
            const nextWidth = Math.floor(entry.contentRect.width);

            // This reads the available height
            const nextHeight = Math.floor(entry.contentRect.height);

            // This avoids pointless state updates
            setStageBoundsPx((previousValue) => {

                // Reuse previous state if nothing changed
                if (previousValue.width === nextWidth && previousValue.height === nextHeight) {

                    return previousValue;

                }

                // Store the new bounds
                return {
                    width: nextWidth,
                    height: nextHeight,
                };

            });

        });

        // This starts observing
        observer.observe(node);

        // This cleans up
        return () => {

            // This disconnects the observer
            observer.disconnect();

        };

    }, []);



    // Compute how the real canvas fits inside the available stage
    const containMetrics = useMemo(() => {

        // Guard until we have a measured size
        if (!stageBoundsPx.width || !stageBoundsPx.height) {

            // This returns safe defaults
            return {
                scale: 1,
                offsetLeft: 0,
                offsetTop: 0,
                renderedW: 0,
                renderedH: 0,
            };

        }

        // This reads a safe canvas width
        const safeCanvasWidth = Number(canvasWidth || 1);

        // This reads a safe canvas height
        const safeCanvasHeight = Number(canvasHeight || 1);

        // This computes contain scale against both available width and height
        const scale = Math.min(
            stageBoundsPx.width / safeCanvasWidth,
            stageBoundsPx.height / safeCanvasHeight
        );

        // This computes rendered width
        const renderedW = safeCanvasWidth * scale;

        // This computes rendered height
        const renderedH = safeCanvasHeight * scale;

        // This centers horizontally
        const offsetLeft = (stageBoundsPx.width - renderedW) / 2;

        // This centers vertically
        const offsetTop = (stageBoundsPx.height - renderedH) / 2;

        // This returns metrics
        return {
            scale: scale,
            offsetLeft: offsetLeft,
            offsetTop: offsetTop,
            renderedW: renderedW,
            renderedH: renderedH,
        };

    }, [
        stageBoundsPx,
        canvasWidth,
        canvasHeight,
    ]);



    // This is the correct canvas->pixel scale for positioning math
    const canvasToPixelScale = containMetrics.scale;



    // This stores whether the image is selected
    const [isSelected, setIsSelected] = useState(false);

    // This selects the image
    const select = useCallback(() => {

        // Select the image
        setIsSelected(true);

    }, []);

    // This deselects the image
    const deselect = useCallback(() => {

        // Deselect the image
        setIsSelected(false);

    }, []);

    // This stores drag state
    const dragStateRef = useRef(null);



    // This wires up arrow keys only when the image is selected
    useArrowKeyNudge({
        isEnabled: isSelected,
        onNudge: ({ deltaX, deltaY }) => {

            // This nudges the transform
            nudgeTransform({
                deltaX: deltaX,
                deltaY: deltaY,
            });

        },
        step: 0.01,
        shiftStep: 0.03,
    });



    // This reads the background image url
    const backgroundUrl = backgroundLayer?.image_url || "";

    // This reads the trim image url
    const trimUrl = trimLayer?.image_url || "";



    // This reads the current render centre x in stage pixels
    const renderCentreXPx =
        (Number(positionBounds.topLeftX || 0) + (Number(transform?.centre_x ?? 0.5) * Number(positionBounds.width || 0))) * canvasToPixelScale;

    // This reads the current render centre y in stage pixels
    const renderCentreYPx =
        (Number(positionBounds.topLeftY || 0) + (Number(transform?.centre_y ?? 0.5) * Number(positionBounds.height || 0))) * canvasToPixelScale;

    // This reads a safe natural image width
    const safeNaturalWidth = Number(naturalWidth || effectiveAsset?.natural_width || 1);

    // This reads a safe natural image height
    const safeNaturalHeight = Number(naturalHeight || effectiveAsset?.natural_height || 1);

    // This reads the image aspect ratio
    const imageAspectRatio = safeNaturalWidth / safeNaturalHeight;

    // This reads the position aspect ratio
    const positionAspectRatio = Number(positionBounds.width || 1) / Number(positionBounds.height || 1);

    // This stores the base width before user scaling
    let baseRenderWidthPx = 0;

    // This stores the base height before user scaling
    let baseRenderHeightPx = 0;

    // This works out the base size for the live fit/fill mode
    if (transform?.fit_fill === "fill") {

        // Fill means cover the whole print area
        if (imageAspectRatio > positionAspectRatio) {

            // Wide image: match height, overflow width
            baseRenderHeightPx = Number(positionBounds.height || 0) * canvasToPixelScale;
            baseRenderWidthPx = baseRenderHeightPx * imageAspectRatio;

        } else {

            // Tall image: match width, overflow height
            baseRenderWidthPx = Number(positionBounds.width || 0) * canvasToPixelScale;
            baseRenderHeightPx = baseRenderWidthPx / imageAspectRatio;

        }

    } else {

        // Fit means contain fully inside the print area
        if (imageAspectRatio > positionAspectRatio) {

            // Wide image: match width, shrink height
            baseRenderWidthPx = Number(positionBounds.width || 0) * canvasToPixelScale;
            baseRenderHeightPx = baseRenderWidthPx / imageAspectRatio;

        } else {

            // Tall image: match height, shrink width
            baseRenderHeightPx = Number(positionBounds.height || 0) * canvasToPixelScale;
            baseRenderWidthPx = baseRenderHeightPx * imageAspectRatio;

        }

    }

    // This reads the current rendered width in stage pixels
    const renderWidthPx =
        baseRenderWidthPx * Number(transform?.scale_x ?? 1);

    // This reads the current rendered height in stage pixels
    const renderHeightPx =
        baseRenderHeightPx * Number(transform?.scale_y ?? 1);



    // This reads the current rotation value
    const renderRotateDeg = Number(transform?.rotation ?? 0);

    // This reads the current scale as a percentage
    const currentScalePercent =
        Math.round(Number(transform?.scale_x || 1) * 100);


    // This sets an exact scale percentage
    const handleSetScalePercent = (scalePercent) => {

        // Convert the requested percentage safely
        const safeScalePercent = Number(scalePercent);

        // Stop when the requested percentage is invalid
        if (!Number.isFinite(safeScalePercent)) {
            return;
        }

        // Convert the supported scale limits into percentages
        const minimumScalePercent =
            Math.round(MIN_SCALE * 100);

        const maximumScalePercent =
            Math.round(MAX_SCALE * 100);

        // Keep the requested percentage inside the supported range
        const clampedScalePercent = Math.max(
            minimumScalePercent,
            Math.min(maximumScalePercent, safeScalePercent)
        );

        // Store the percentage as the underlying decimal scale
        setUniformScale(clampedScalePercent / 100);

    };


    // This adjusts the current size by an exact number of percentage points
    const handleNudgeScalePercent = (changePercent) => {

        // Apply the requested change to the displayed percentage
        handleSetScalePercent(
            currentScalePercent + Number(changePercent)
        );

    };


    // This renders the precise size controls
    const RenderScaleNudgeButtons = () => {

        // Return controls matching the rotation controls
        return (

            <div className="imagePlacementEditor__rotationGrid">

                <CreateButton 
                    title="<<"
                    size="xs"
                    plain
                    tooltip="Reduce size by 10 percentage points"
                    onClick={() => handleNudgeScalePercent(-10)}
                />

                <CreateButton 
                    title="<"
                    size="xs"
                    plain
                    tooltip="Reduce size by 1 percentage point"
                    onClick={() => handleNudgeScalePercent(-1)}
                />
                
                <CreateButton 
                    title="100"
                    size="xs"
                    plain
                    tooltip="Reset size to 100%"
                    onClick={() => handleSetScalePercent(100)}
                />

                <CreateButton 
                    title=">"
                    size="xs"
                    plain
                    tooltip="Increase size by 1 percentage point"
                    onClick={() => handleNudgeScalePercent(1)}
                />

                <CreateButton 
                    title=">>"
                    size="xs"
                    plain
                    tooltip="Increase size by 10 percentage points"
                    onClick={() => handleNudgeScalePercent(10)}
                />

            </div>

        );

    };


    // This reads the position default label
    const defaultPositionLabel = childSku ? "Inherit from parent" : "Not set";



    // This resets the placement override and inherited/default settings
    const handleResetPlacement = useCallback(() => {

        // This clears selected position to default
        setSelectedPositionCode(null);

        // This forces current fit/fill automatic position logic
        setUseAutomaticPosition(true);

        // This clears selected background colour to default
        setBackgroundColour(null);

        // This clears the saved position value
        setPosition(null);

        // This resets the transform
        resetTransform();

    }, [
        setBackgroundColour,
        setPosition,
        resetTransform,
    ]);



    // This exposes the reset handler to the parent popup footer
    useEffect(() => {

        // Stop when the parent has not requested the reset handler
        if (typeof onResetPlacementReady !== "function") {

            return;

        }

        // Register this editor's reset handler
        onResetPlacementReady(handleResetPlacement);

        // Clear the registered handler when this editor unmounts
        return () => {

            onResetPlacementReady(null);

        };

    }, [
        onResetPlacementReady,
        handleResetPlacement,
    ]);



    // This selects a preferred fit/fill position and centres the design at default size
    const handleApplyPositionPreset = (positionMode) => {

        // Work out which preferred position flag to use
        const preferredPositionKey = positionMode === "fill" ? "preferred_fill" : "preferred_fit";

        // Find the matching preferred position, falling back to the first available position
        const preferredPosition = positions.find((positionItem) => positionItem?.[preferredPositionKey] === "Y") || positions?.[0] || null;

        // Read the matching position code
        const preferredPositionCode = normalisePositionValue(preferredPosition?.code || null);

        // Store the selected position code
        setSelectedPositionCode(preferredPositionCode);

        // This is now an explicit position choice, not automatic inheritance
        setUseAutomaticPosition(false);

        // Update the outgoing position payload
        setPosition(preferredPositionCode);

        // Change the live fit/fill mode, centre the design and reset its manual scale
        applyTransformPatch({
            fit_fill: positionMode,
            centre_x: 0.5,
            centre_y: 0.5,
            scale_x: 1,
            scale_y: 1,
        });

    };



    // This applies an explicit rotation value
    const handleSetRotation = (nextRotation) => {

        // This sets the rotation
        setRotate(nextRotation);

    };



    // This starts dragging the image
    const onDragPointerDown = (event) => {

        // This stops bubbling
        event.stopPropagation();

        // This selects the image
        select();

        // This reads the starting pointer position
        const startClientX = event.clientX;

        // This reads the starting pointer position
        const startClientY = event.clientY;

        // This reads the starting centre
        const startCentreX = Number(transform?.centre_x ?? 0.5);

        // This reads the starting centre
        const startCentreY = Number(transform?.centre_y ?? 0.5);

        // This stores drag state
        dragStateRef.current = {
            startClientX: startClientX,
            startClientY: startClientY,
            startCentreX: startCentreX,
            startCentreY: startCentreY,
        };

        // This handles pointer move
        const handlePointerMove = (moveEvent) => {

            // This reads drag state
            const dragState = dragStateRef.current;

            // This guards
            if (!dragState) return;

            // This converts movement into clip-space percentages
            const deltaXPx = moveEvent.clientX - dragState.startClientX;

            // This converts movement into clip-space percentages
            const deltaYPx = moveEvent.clientY - dragState.startClientY;

            // This reads clip width in pixels
            const clipWidthPx = Number(positionBounds.width || 1) * canvasToPixelScale;

            // This reads clip height in pixels
            const clipHeightPx = Number(positionBounds.height || 1) * canvasToPixelScale;

            // This converts x to centre percentage
            const deltaXPercent = clipWidthPx ? (deltaXPx / clipWidthPx) : 0;

            // This converts y to centre percentage
            const deltaYPercent = clipHeightPx ? (deltaYPx / clipHeightPx) : 0;

            // This updates centre x
            setCentreX(dragState.startCentreX + deltaXPercent);

            // This updates centre y
            setCentreY(dragState.startCentreY + deltaYPercent);

        };

        // This handles pointer up
        const handlePointerUp = () => {

            // This clears drag state
            dragStateRef.current = null;

            // This removes listeners
            window.removeEventListener("pointermove", handlePointerMove);

            // This removes listeners
            window.removeEventListener("pointerup", handlePointerUp);

        };

        // This adds listeners
        window.addEventListener("pointermove", handlePointerMove);

        // This adds listeners
        window.addEventListener("pointerup", handlePointerUp);

    };



    // This gets a pointer point in local editor pixels
    const getLocalPointPx = (event) => {

        // This reads the editor bounds
        const editorRect = editorRef.current?.getBoundingClientRect();

        // This returns safe zeroes if the editor is missing
        if (!editorRect) {

            // This returns zero point
            return {
                x: 0,
                y: 0,
            };

        }

        // This converts client coordinates into local editor coordinates
        return {

            // This is x in local editor pixels
            x: event.clientX - editorRect.left,

            // This is y in local editor pixels
            y: event.clientY - editorRect.top,

        };

    };



    // Handle pointer down for corner handles only
    const onHandlePointerDown = (event, handle) => {

        // Prevent default
        event.preventDefault();

        // Stop propagation
        event.stopPropagation();

        // Select the image
        select();

        // Only allow corner handles
        const isCorner = handle === "tl" || handle === "tr" || handle === "bl" || handle === "br";

        // Guard
        if (!isCorner) return;

        // Read start pointer in local editor pixels
        const startPx = getLocalPointPx(event);

        // Read position width
        const posW = Number(positionBounds?.width || 1);

        // Read position height
        const posH = Number(positionBounds?.height || 1);

        // Compute centre in canvas units
        const centerCanvasX =
            Number(positionBounds?.topLeftX || 0) +
            (Number(transform?.centre_x ?? 0.5) * posW);

        // Compute centre in canvas units
        const centerCanvasY =
            Number(positionBounds?.topLeftY || 0) +
            (Number(transform?.centre_y ?? 0.5) * posH);

        // Convert centre to local editor pixels
        const centerLocalPx = {

            // X in px
            x: centerCanvasX * Number(canvasToPixelScale || 1),

            // Y in px
            y: centerCanvasY * Number(canvasToPixelScale || 1),

        };

        // Compute dx from centre
        const dx = startPx.x - centerLocalPx.x;

        // Compute dy from centre
        const dy = startPx.y - centerLocalPx.y;

        // Compute start distance
        const startDistancePx = Math.max(1, Math.sqrt((dx * dx) + (dy * dy)));

        // Store resize state
        resizeRef.current = {

            // Start distance
            startDistancePx: startDistancePx,

            // Start scale
            startScaleX: Number(transform?.scale_x || 1),

            // Centre local px
            centerLocalPx: centerLocalPx,

        };

        // Handle pointer move
        const handlePointerMove = (moveEvent) => {

            // Read active resize state
            const resizeState = resizeRef.current;

            // Guard
            if (!resizeState) return;

            // Read current pointer in local editor pixels
            const currentPx = getLocalPointPx(moveEvent);

            // Compute dx from centre
            const moveDx = currentPx.x - resizeState.centerLocalPx.x;

            // Compute dy from centre
            const moveDy = currentPx.y - resizeState.centerLocalPx.y;

            // Compute current distance
            const currentDistancePx = Math.max(
                1,
                Math.sqrt((moveDx * moveDx) + (moveDy * moveDy))
            );

            // Compute next scale from start ratio
            const nextScale = Math.max(
                MIN_SCALE,
                Math.min(
                    MAX_SCALE,
                    resizeState.startScaleX * (currentDistancePx / resizeState.startDistancePx)
                )
            );

            // Update uniform scale
            setUniformScale(nextScale);

        };

        // Handle pointer up
        const handlePointerUp = () => {

            // Clear resize state
            resizeRef.current = null;

            // Remove listeners
            window.removeEventListener("pointermove", handlePointerMove);

            // Remove listeners
            window.removeEventListener("pointerup", handlePointerUp);

        };

        // Add listeners
        window.addEventListener("pointermove", handlePointerMove);

        // Add listeners
        window.addEventListener("pointerup", handlePointerUp);

    };



    // This starts rotating from the rotate handle
    const handleRotatePointerDown = useCallback((event) => {

        // This prevents default browser behaviour
        event.preventDefault();

        // This stops parent drag/select behaviour
        event.stopPropagation();

        // This keeps the native event from bubbling further
        if (event.nativeEvent?.stopImmediatePropagation) {
            event.nativeEvent.stopImmediatePropagation();
        }

        // This selects the image
        select();

        // This marks rotating as active
        setIsRotating(true);

    }, [select]);



    // This handles rotate dragging while active
    useEffect(() => {

        // This exits if rotation is not active
        if (!isRotating) {

            // This stops here
            return;

        }

        // This updates rotation from the current pointer
        const handlePointerMove = (event) => {

            // This exits if there is no selection overlay ref
            if (!selectionRef.current) {

                // This stops here
                return;

            }

            // This gets the selection bounds
            const wrapperBounds = selectionRef.current.getBoundingClientRect();

            // This works out the centre x of the selection overlay
            const centreX = wrapperBounds.left + (wrapperBounds.width / 2);

            // This works out the centre y of the selection overlay
            const centreY = wrapperBounds.top + (wrapperBounds.height / 2);

            // This gets the pointer x position
            const pointerX = event.clientX;

            // This gets the pointer y position
            const pointerY = event.clientY;

            // This calculates the angle from centre to pointer
            const nextAngle = getAngleFromPoints({
                centreX: centreX,
                centreY: centreY,
                pointerX: pointerX,
                pointerY: pointerY,
            });

            // This snaps the dragged angle
            const snappedAngle = event.shiftKey
                ? snapRotateDegrees(nextAngle, 45)
                : snapRotateDegrees(nextAngle, 1);

            // This updates the rotate value
            setRotate(snappedAngle);

        };

        // This stops rotating
        const handlePointerUp = () => {

            // This clears rotating state
            setIsRotating(false);

            // This clears rotate ref
            rotateRef.current = null;

        };

        // This listens for pointer movement on the window
        window.addEventListener("pointermove", handlePointerMove);

        // This listens for pointer end on the window
        window.addEventListener("pointerup", handlePointerUp);

        // This cleans up global listeners
        return () => {

            // This removes the move listener
            window.removeEventListener("pointermove", handlePointerMove);

            // This removes the up listener
            window.removeEventListener("pointerup", handlePointerUp);

        };

    }, [
        isRotating,
        setRotate,
    ]);




    // This handles selecting a position
    const handleSelectPosition = (nextPositionCode) => {

        // This normalises the value
        const normalisedPositionCode = normalisePositionValue(nextPositionCode);

        // This stores the selected position code
        setSelectedPositionCode(normalisedPositionCode);

        // Manual selection means no longer using reset automatic mode
        setUseAutomaticPosition(false);

        // This updates the transform payload
        setPosition(nextPositionCode);

    };




    // Render the positions list
    const RenderListPositions = ({
        positions: positionList,
        defaultLabel = "Not set",
    }) => {

        // Default is selected whenever no explicit position exists
        const defaultIsSelected = useAutomaticPosition || selectedPositionCode === null;

        // This returns the position list
        return (

            <div className="image-editor__positions-dropdown">

                <button
                    type="button"
                    className={`image-editor__positions-list-item ${defaultIsSelected ? "image-editor__positions-list-item__selected" : ""}`}
                    onClick={() => handleSelectPosition(null)}
                >
                    {defaultLabel}
                </button>

                {(positionList || []).map((positionItem) => (

                    <button
                        key={positionItem?.code || ""}
                        type="button"
                        className={`image-editor__positions-list-item ${positionItem?.code === selectedPositionCode ? "image-editor__positions-list-item__selected" : ""}`}
                        onClick={() => handleSelectPosition(positionItem?.code || null)}
                    >

                        {positionItem?.name || positionItem?.code || ""}

                    </button>

                ))}

            </div>

        );

    };



    // Render one editor panel shell
    const RenderEditorPanel = ({
        title,
        children,
    }) => {

        // Return the panel
        return (

            <section className="imagePlacementEditor__panel">

                <div className="imagePlacementEditor__panelHeader">
                    {title}
                </div>

                <div className= {`${title === "Position" ? "" : "imagePlacementEditor__panelBody"}`}>
                    {children}
                </div>

            </section>
        );

    };



    // Render the left panel column
    const RenderLeftPanels = () => {

        // Return the left panels
        return (

            <div className="imagePlacementEditor__sidePanels imagePlacementEditor__sidePanels--left">

                <RenderEditorPanel title="Position">

                    <RenderListPositions
                        positions={positions}
                        defaultLabel={defaultPositionLabel}
                    />

                </RenderEditorPanel>
                
                {/* Can't display in <RenderEditorPanel /> function - stops slider from working */}
                <section className="imagePlacementEditor__panel">

                    <div className="imagePlacementEditor__panelHeader">
                        Background Colour
                    </div>

                    <div className="imagePlacementEditor__panelBody">

                        <div className="imagePlacementEditor__colourControl">

                            <ColorPickerSmall
                                label=""
                                title="Background Colour"
                                size="l"
                                value={transform.edited_background === "N" ? null : transform?.background_colour ?? resolvedBackgroundColour}
                                forceOpen={true}
                                renderInline={true}
                                allowNullOrTransparent={true}
                                onChange={(hexOrNull) => {

                                    // This clears the explicit colour
                                    if (hexOrNull === "reset" || hexOrNull === null) {

                                        // This resets to null
                                        setBackgroundColour(null);

                                        // This stops here
                                        return;

                                    }

                                    // This stores the chosen colour
                                    setBackgroundColour(hexOrNull);

                                }}
                                setClearColourAsTransparent={false}
                            />

                        </div>

                    </div>

                </section>
                
            </div>

        );

    };



    // Render the right panel column
    const RenderRightPanels = () => {

        // Return the right panels
        return (

            <div className="imagePlacementEditor__sidePanels imagePlacementEditor__sidePanels--right">

                {/* Can't display in <RenderEditorPanel /> function - stops slider from working */}
                <section className="imagePlacementEditor__panel">

                    <div className="imagePlacementEditor__panelHeader">
                        Size
                    </div>

                    <div className="imagePlacementEditor__panelBody">
                        
                        <div className="imagePlacementEditor__panelValue">
                            {currentScalePercent}%
                        </div>
                    
                        <input
                            className="imagePlacementEditor__scaleRange"
                            type="range"
                            min="0"
                            max={SLIDER_MAX_VALUE}
                            step="1"
                            value={Math.round(scaleToSliderValue(transform?.scale_x ?? 1))}
                            onInput={(event) => {

                                // This reads the live slider value
                                const nextSliderValue = Number(event.currentTarget.value);

                                // This converts the slider value into a real scale
                                const nextScale = sliderValueToScale(nextSliderValue);

                                // This updates the image scale
                                setUniformScale(nextScale);

                            }}
                        />

                        <RenderScaleNudgeButtons />

                        <div className="imagePlacementEditor__fitFillButtons">

                            <CreateButton 
                                title="Fit"
                                size="xs"
                                plain
                                tooltip="Size to fit"
                                onClick={() => handleApplyPositionPreset("fit")}
                            />

                            <CreateButton 
                                title="Fill"
                                size="xs"
                                plain
                                tooltip="Size to fill"
                                onClick={() => handleApplyPositionPreset("fill")}
                            />

                        </div>

                    </div>

                </section>

                <RenderEditorPanel title="Rotation">

                    <div className="imagePlacementEditor__panelValue">
                        {Math.round(renderRotateDeg)}°
                    </div>

                    <div className="imagePlacementEditor__rotationGrid">

                        <CreateButton 
                            title="<<"
                            size="xs"
                            plain
                            tooltip="Rotate left 90"
                            onClick={() => handleSetRotation(renderRotateDeg - 90)}
                        />

                        <CreateButton 
                            title="<"
                            size="xs"
                            plain
                            tooltip="Rotate left 15°"
                            onClick={() => handleSetRotation(renderRotateDeg - 15)}
                        />
                        
                        <CreateButton 
                            title="0"
                            size="xs"
                            plain
                            tooltip="Reset rotation"
                            onClick={() => handleSetRotation(0)}
                        />

                        <CreateButton 
                            title=">"
                            size="xs"
                            plain
                            tooltip="Rotate right 15°"
                            onClick={() => handleSetRotation(renderRotateDeg + 15)}
                        />

                        <CreateButton 
                            title=">>"
                            size="xs"
                            plain
                            tooltip="Rotate right 90°"
                            onClick={() => handleSetRotation(renderRotateDeg + 90)}
                        />
                       
                    </div>

                </RenderEditorPanel>

            </div>
        );

    };



    // Render the top tool bar on mobile view
    const RenderMobileToolBar = () => {

        return (

            <div className="horizontal-align-content-center">

                <select 
                    defaultValue={selectedPositionCode || null} 
                    onChange={(e) => handleSelectPosition(e.target.value || null)}
                    style={{width: "auto"}}
                >

                    <option value={null}>Unset position</option>

                    {(positions || []).map((positionItem) => (

                        <option
                            key={positionItem?.code || ""}
                            name={positionItem?.name}
                            value={positionItem?.code}
                            className={`image-editor__positions-list-item ${positionItem?.code === selectedPositionCode ? "image-editor__positions-list-item__selected" : ""}`}
                            
                        >

                            {positionItem?.name || positionItem?.code || ""}

                        </option>

                    ))}

                </select>


                <ColorPickerSmall
                    label=""
                    title="Background Colour"
                    size="l"
                    value={transform.edited_background === "N" ? null : transform?.background_colour ?? resolvedBackgroundColour}
                    allowNullOrTransparent={true}
                    onChange={(hexOrNull) => {

                        // This clears the explicit colour
                        if (hexOrNull === "reset" || hexOrNull === null) {

                            // This resets to null
                            setBackgroundColour(null);

                            // This stops here
                            return;

                        }

                        // This stores the chosen colour
                        setBackgroundColour(hexOrNull);

                    }}
                    setClearColourAsTransparent={false}
                />


                <div className="imagePlacementEditor__mobilePanelValue">
                    {currentScalePercent}%
                </div>
            
                <input
                    className="imagePlacementEditor__scaleRange"
                    style={{maxWidth: "30%"}}
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(scaleToSliderValue(transform?.scale_x ?? 1))}
                    onInput={(event) => {

                        // This reads the live slider value
                        const nextSliderValue = Number(event.currentTarget.value);

                        // This converts the slider value into a real scale
                        const nextScale = sliderValueToScale(nextSliderValue);

                        // This updates the image scale
                        setUniformScale(nextScale);

                    }}
                />

                <RenderScaleNudgeButtons />

                <div className="imagePlacementEditor__fitFillButtons">

                    <CreateButton
                        title="Fit"
                        tooltip="Use fit position"
                        size="xs"
                        type="button"
                        plain={true}
                        onClick={() => handleApplyPositionPreset("fit")}
                    />

                    <CreateButton
                        title="Fill"
                        tooltip="Use fill position"
                        size="xs"
                        type="button"
                        plain={true}
                        onClick={() => handleApplyPositionPreset("fill")}
                    />

                </div>

                <div className="imagePlacementEditor__mobilePanelValue">

                    {Math.round(renderRotateDeg)}°

                </div>

                <div className="imagePlacementEditor__rotationGrid">

                    <CreateButton title="<<" size="xs" tooltip="Rotate left 90°" plain={true} onClick={() => handleSetRotation(renderRotateDeg - 90)} />
                    <CreateButton title="<" size="xs" tooltip="Rotate left 15°" plain={true} onClick={() => handleSetRotation(renderRotateDeg - 15)} />
                    <CreateButton title="0" size="xs" tooltip="Reset rotation" plain={true} onClick={() => handleSetRotation(0)} />
                    <CreateButton title=">" size="xs" tooltip="Rotate right 15°" plain={true} onClick={() => handleSetRotation(renderRotateDeg + 15)} />
                    <CreateButton title=">>" size="xs" tooltip="Rotate right 90°" plain={true} onClick={() => handleSetRotation(renderRotateDeg + 90)} />

                </div>

            </div>

        )

    };



    // If there is still no asset then render loading
    if (!effectiveAsset) {

        // This returns loading UI
        return (

            <div className="imagePlacementEditor">

                <div className="imagePlacementEditor__stage">

                    Loading...

                </div>

            </div>

        );
        
    };



    // This returns the component
    return (

        <div className="imagePlacementEditor">

            <div className="imagePlacementEditor__editorGrid">

                {!isMobile && RenderLeftPanels()}


                {isMobile && RenderMobileToolBar()}

                <div
                    className="imagePlacementEditor__stage"
                    ref={stageRef}
                    onPointerDown={(event) => {

                        // This reads the clicked node
                        const targetNode = event.target;

                        // This keeps clicks inside the user image
                        if (userRef.current && userRef.current.contains(targetNode)) {
                            return;
                        }

                        // This keeps clicks inside the selection overlay
                        if (selectionRef.current && selectionRef.current.contains(targetNode)) {
                            return;
                        }

                        // This keeps clicks inside the rotate handle
                        if (rotateHandleRef.current && rotateHandleRef.current.contains(targetNode)) {
                            return;
                        }

                        // This deselects otherwise
                        deselect();

                    }}
                >

                    <div className="imagePlacementEditor__canvas">

                        <div
                            className="imagePlacementEditor__canvasViewport"
                            ref={editorRef}
                            style={{
                                left: `${containMetrics.offsetLeft}px`,
                                top: `${containMetrics.offsetTop}px`,
                                width: `${containMetrics.renderedW}px`,
                                height: `${containMetrics.renderedH}px`,
                            }}
                        >

                            {backgroundUrl ? (

                                <img
                                    className="imagePlacementEditor__layer imagePlacementEditor__layer--background"
                                    src={backgroundUrl}
                                    alt="Background"
                                    draggable={false}
                                />

                            ) 
                            
                                : null
                            
                            }



                            {/* This groups the print colour and user artwork together */}
                            <div
                                className="imagePlacementEditor__printGroup"
                                style={{
                                    mixBlendMode: printAreaSettings?.composite_mode ? printAreaSettings.composite_mode : "",
                                }}
                            >

                                <div 
                                    className="imagePlacementEditor__backgroundColour"
                                    style={{
                                        backgroundColor: transform?.edited_background === "N" ? "transparent" : transform?.background_colour ?? resolvedBackgroundColour ?? "transparent",
                                    }}
                                >
                                </div>

                                <div
                                    className="imagePlacementEditor__userClip"
                                    style={{
                                        left: `${Number(positionBounds.topLeftX || 0) * canvasToPixelScale}px`,
                                        top: `${Number(positionBounds.topLeftY || 0) * canvasToPixelScale}px`,
                                        width: `${Number(positionBounds.width || 0) * canvasToPixelScale}px`,
                                        height: `${Number(positionBounds.height || 0) * canvasToPixelScale}px`,
                                    }}
                                >

                                    <div
                                        className="imagePlacementEditor__user"
                                        ref={userRef}
                                        style={{
                                            left: `${renderCentreXPx - (Number(positionBounds.topLeftX || 0) * canvasToPixelScale)}px`,
                                            top: `${renderCentreYPx - (Number(positionBounds.topLeftY || 0) * canvasToPixelScale)}px`,
                                            transform: `translate(-50%, -50%) rotate(${renderRotateDeg}deg)`,
                                            width: `${renderWidthPx}px`,
                                            height: `${renderHeightPx}px`,
                                        }}
                                        onPointerDown={(event) => {

                                            // This stops stage deselect
                                            event.stopPropagation();

                                            // This selects the image
                                            select();

                                            // This starts drag
                                            onDragPointerDown(event);

                                        }}
                                    >

                                        <img
                                            className="imagePlacementEditor__userImage"
                                            src={userImageUrl || ""}
                                            alt="User design"
                                            draggable={false}
                                        />

                                    </div>

                                </div>

                            </div>

                            {trimUrl ? (

                                <img
                                    className="imagePlacementEditor__layer imagePlacementEditor__layer--trim"
                                    src={trimUrl}
                                    alt="Trim overlay"
                                    draggable={false}
                                />

                            ) 
                            
                                : null
                            
                            }

                            {isSelected && (

                                <div
                                    className="imagePlacementEditor__selectionOverlay"
                                    ref={selectionRef}
                                    onPointerDown={(event) => {

                                        // This stops bubbling so stage never deselects
                                        event.stopPropagation();

                                    }}
                                    style={{
                                        left: `${renderCentreXPx}px`,
                                        top: `${renderCentreYPx}px`,
                                        transform: `translate(-50%, -50%) rotate(${renderRotateDeg}deg)`,
                                        width: `${renderWidthPx}px`,
                                        height: `${renderHeightPx}px`,
                                    }}
                                >

                                    <div className="imagePlacementEditor__selectionBorder" />

                                    <button
                                        ref={rotateHandleRef}
                                        className="imagePlacementEditor__rotateHandle"
                                        onPointerDown={handleRotatePointerDown}
                                        title="Rotate image"
                                        type="button"
                                    >

                                        ↻

                                    </button>

                                    <div
                                        className="imagePlacementEditor__handle imagePlacementEditor__handle--tl"
                                        onPointerDown={(event) => onHandlePointerDown(event, "tl")}
                                        style={{
                                            cursor: getRotatedCornerCursor({
                                                rotation: transform?.rotation  ?? 0,
                                                handle: "tl",
                                            }),
                                        }}
                                    />

                                    <div
                                        className="imagePlacementEditor__handle imagePlacementEditor__handle--tr"
                                        onPointerDown={(event) => onHandlePointerDown(event, "tr")}
                                        style={{
                                            cursor: getRotatedCornerCursor({
                                                rotation: transform?.rotation  ?? 0,
                                                handle: "tr",
                                            }),
                                        }}
                                    />

                                    <div
                                        className="imagePlacementEditor__handle imagePlacementEditor__handle--bl"
                                        onPointerDown={(event) => onHandlePointerDown(event, "bl")}
                                        style={{
                                            cursor: getRotatedCornerCursor({
                                                rotation: transform?.rotation  ?? 0,
                                                handle: "bl",
                                            }),
                                        }}
                                    />

                                    <div
                                        className="imagePlacementEditor__handle imagePlacementEditor__handle--br"
                                        onPointerDown={(event) => onHandlePointerDown(event, "br")}
                                        style={{
                                            cursor: getRotatedCornerCursor({
                                                rotation: transform?.rotation  ?? 0,
                                                handle: "br",
                                            }),
                                        }}
                                    />

                                </div>

                            )}

                        </div>

                    </div>

                </div>


                {!isMobile && RenderRightPanels()}

            </div>

        </div>

    );

};